import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Spin, Tabs } from "antd";

import { all_routes } from "../../../../../router/all_routes";
import CommonSelect from "../../../../../../core/common/commonSelect";
import CollapseHeader from "../../../../../../core/common/collapse-header/collapse-header";
import { getAllComapniesList } from "../../../../../../services/attendanceService";
import { showBasicAlert } from "../../../../../../core/common/useAlert";

import LeaveTypesTab from "../tabs/LeaveTypesTab";
import AttendanceBalanceTab from "../tabs/AttendanceBalanceTab";

import "../styles/leaveSettings.css";

type Company = {
  id: number;
  name: string;
  type: string;
};

type CompanyOption = {
  value: string;
  label: string;
  type: string;
};

const ADMIN_ROLES = new Set(["ADMIN", "EMPLOYEEADMIN"]);

const LeaveSettings: React.FC = () => {

  const userRole = useSelector(
    (state: any) => {
      return (
        (state.user?.user?.role_char || state.user?.user?.role || state.user?.user?.userDetails?.role_char || "")
          .toString()
          .toUpperCase()
      )
    }
  );

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(
    null,
  );
  const [companiesLoading, setCompaniesLoading] = useState<boolean>(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get("tab") || "types"
  );

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const res = await getAllComapniesList();
      const list: CompanyOption[] = res.data.data.data.map(
        (company: Company) => ({
          value: company.id.toString(),
          label: company.name + " - " + company.type,
          type: company.type,
        }),
      );
      setCompanies(list);
      if (list.length > 0) {
        setSelectedCompany(list[0]);
      }
    } catch (error) {
      showBasicAlert("Failed to load companies");
    } finally {
      setCompaniesLoading(false);
    }
  };

  const tabItems = useMemo(
    () => [
      {
        key: "types",
        label: (
          <span className="d-flex align-items-center">
            <i className="ti ti-list-details me-2" />
            Leave Types
          </span>
        ),
        children: <LeaveTypesTab selectedCompany={selectedCompany} />,
      },
      {
        key: "balance",
        label: (
          <span className="d-flex align-items-center">
            <i className="ti ti-adjustments me-2" />
            Leaves Balance
          </span>
        ),
        children: <AttendanceBalanceTab selectedCompany={selectedCompany} />,
      },
    ],
    [selectedCompany],
  );

  if (!userRole || !ADMIN_ROLES.has(userRole)) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <h4 className="text-muted">
            You do not have permission to view this page.
          </h4>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper leave-settings">
        <div className="content">
          <div className="ls-header card border-0 mb-3">
            <div className="ls-header__inner d-md-flex d-block align-items-center justify-content-between">
              <div className="my-auto mb-3 mb-md-0">
                <h2 className="mb-1 d-flex align-items-center gap-2">
                  <span className="ls-title-dot" />
                  Leave Settings
                </h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.adminDashboard}>
                        <i className="ti ti-smart-home" />
                      </Link>
                    </li>
                    <li className="breadcrumb-item">Employee</li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Leave Settings
                    </li>
                  </ol>
                </nav>
              </div>

              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center">
                  <CommonSelect
                    options={companies}
                    defaultValue={selectedCompany || undefined}
                    onChange={(opt: any) => setSelectedCompany(opt)}
                    className="select-filter"
                  />
                </div>

                <div className="head-icons ms-2">
                  <CollapseHeader />
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body bg-white">
              {companiesLoading && !selectedCompany ? (
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ minHeight: 280 }}
                >
                  <Spin size="large" />
                </div>
              ) : (
                <Tabs
                  activeKey={activeTab}
                  onChange={(key) => {
                    setActiveTab(key);
                    setSearchParams({ tab: key });
                  }}
                  type="line"
                  size="middle"
                  className="modern-tabs"
                  items={tabItems}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeaveSettings;
